import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import {
  MapPin,
  Clock,
  Calendar,
  Phone,
  WarningCircle,
  CheckCircle,
  QrCode,
  X,
  Compass,
  ArrowSquareOut,
  Lock,
  Users,
  CreditCard,
} from "@phosphor-icons/react";
import { useQuery, useMutation } from "convex/react";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import { QRCodeSVG } from "qrcode.react";
import { getCurrentLanguage } from "@shared/lib/i18n";
import { getLocalizedText } from "@shared/lib/utils";
import { trackEvent } from "@shared/lib/posthog-lazy";
import { OfferDetailHeader } from "@shared/components/AppHeader/OfferDetailHeader";
import { USER_SESSION_KEY } from "@shared/hooks/useUserSession";

export const Route = createFileRoute("/app/offers/$offerId")({
  component: OfferDetailPage,
});

function OfferDetailPage() {
  const { offerId } = Route.useParams();
  const navigate = useNavigate();
  const currentLang = getCurrentLanguage();
  const [userId, setUserId] = useState<Id<"users"> | null>(null);
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [showQR, setShowQR] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [qrToken, setQrToken] = useState<string | null>(null);
  const [showNavigationOptions, setShowNavigationOptions] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [friendPhone, setFriendPhone] = useState("");
  const [isSendingInvite, setIsSendingInvite] = useState(false);
  const [showWarning, setShowWarning] = useState(false);

  // Citește userId din localStorage
  useEffect(() => {
    const storedUserId = localStorage.getItem("userId");
    setSessionToken(localStorage.getItem(USER_SESSION_KEY));
    if (storedUserId) {
      setUserId(storedUserId as Id<"users">);
    } else {
      navigate({ to: "/auth/login" });
    }
  }, [navigate]);

  // Obține detaliile ofertei
  const offer = useQuery(
    api.offers.getOfferById,
    { offerId: offerId as Id<"offers"> }
  );

  // Obține datele utilizatorului
  const user = useQuery(
    api.auth.getCurrentUser,
    userId ? { userId } : "skip"
  );

  // Verifică dacă poate folosi oferta
  const canRedeem = useQuery(
    api.redemptions.canUserRedeemOffer,
    userId && sessionToken && offerId
      ? { userId, sessionToken, offerId: offerId as Id<"offers"> }
      : "skip"
  );

  // Obține tokenul activ
  const activeToken = useQuery(
    api.redemptions.getActiveToken,
    userId && sessionToken && offerId && showQR
      ? { userId, sessionToken, offerId: offerId as Id<"offers"> }
      : "skip"
  );

  // Mutații
  const generateToken = useMutation(api.redemptions.generateRedemptionToken);
  const addFavorite = useMutation(api.favorites.addFavorite);
  const removeFavorite = useMutation(api.favorites.removeFavorite);
  const sendPhoneInvite = useMutation(api.invites.sendPhoneInvite);

  // Verifică dacă oferta este favorită
  const isFavorite = useQuery(
    api.favorites.isFavorite,
    userId && sessionToken && offerId
      ? { userId, sessionToken, offerId: offerId as Id<"offers"> }
      : "skip"
  );

  // Obține statusul invitației prin SMS
  const inviteStatus = useQuery(
    api.invites.getPhoneInviteStatus,
    userId && sessionToken ? { userId, sessionToken } : "skip"
  );

  // Countdown timer
  useEffect(() => {
    if (showQR && countdown > 0) {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            setShowQR(false);
            setQrToken(null);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [showQR, countdown]);

  // Sync cu tokenul activ
  useEffect(() => {
    if (activeToken) {
      setQrToken(activeToken.token);
      setCountdown(activeToken.remainingSeconds);
      if (activeToken.remainingSeconds > 0) {
        setShowQR(true);
      }
    }
  }, [activeToken]);

  const handleGenerateQR = async () => {
    if (!userId || !offerId) return;

    // Verifică dacă poate genera QR înainte de a continua
    if (canRedeem?.canRedeem === false) {
      setShowWarning(true);
      return;
    }

    try {
      // Obține locația utilizatorului
      let userLocation: { lat: number; lng: number } | undefined;
      if (navigator.geolocation) {
        try {
          const position = await new Promise<GeolocationPosition>((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, {
              enableHighAccuracy: true,
              timeout: 10000,
              maximumAge: 0,
            });
          });
          userLocation = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
        } catch (geoError) {
          console.warn("Could not get user location:", geoError);
          // Continuă fără locație
        }
      }

      const result = await generateToken({
        userId,
        sessionToken: sessionToken!,
        offerId: offerId as Id<"offers">,
        userLocation,
      });

      if (result.success && result.token) {
        setQrToken(result.token);
        setCountdown(result.durationSeconds || 60);
        setShowQR(true);
        setShowWarning(false); // Ascunde warning-ul dacă QR-ul s-a generat cu succes
        
        // Tracking PostHog
        await trackEvent("qr_token_generated", {
          offerId: offerId,
          partnerId: offer?.partner?._id,
          category: offer?.category?.name,
          discountType: offer?.discountType,
        });
        
        // Verifică dacă utilizatorul este varianta B și tocmai a folosit oferta gratuită
        // Verificăm după un mic delay pentru a permite actualizarea în backend
        setTimeout(() => {
          if (user?.trialVariant === "B" && user?.freeOfferUsed === false) {
            // Verifică din nou după un moment
            setTimeout(() => {
              // Modalul va apărea când QR-ul se închide sau când utilizatorul revine
            }, 2000);
          }
        }, 1000);
      } else {
        // Dacă generarea a eșuat, arată warning-ul
        setShowWarning(true);
      }
    } catch (error) {
      console.error("Error generating QR:", error);
      setShowWarning(true);
    }
  };

  const handleToggleFavorite = async () => {
    if (!userId || !offerId) return;

    try {
      if (isFavorite) {
        await removeFavorite({ userId, sessionToken: sessionToken!, offerId: offerId as Id<"offers"> });
        await trackEvent("offer_removed_from_favorites", {
          offerId: offerId,
          partnerId: offer?.partner?._id,
        });
      } else {
        await addFavorite({ userId, sessionToken: sessionToken!, offerId: offerId as Id<"offers"> });
        await trackEvent("offer_added_to_favorites", {
          offerId: offerId,
          partnerId: offer?.partner?._id,
        });
      }
    } catch (error) {
      console.error("Error toggling favorite:", error);
    }
  };

  const handleSendInvite = async () => {
    if (!userId || !friendPhone.trim()) return;

    setIsSendingInvite(true);
    try {
      const result = await sendPhoneInvite({
        userId,
        sessionToken: sessionToken!,
        friendPhone: friendPhone.trim(),
      });

      if (result.success) {
        // Tracking PostHog
        trackEvent("invite_sent", {
          method: "phone",
          friendPhone: friendPhone.trim(),
          fromOffer: offerId,
        });
        
        if (result.existing) {
          // Invitație existentă - nu închide modalul, doar arată mesaj
          alert(
            currentLang === "ro"
              ? `Invitație existentă pentru ${friendPhone.trim()}. Status: ${
                  result.status === "sent"
                    ? "SMS trimis"
                    : result.status === "registered"
                    ? "Prietenul s-a înregistrat"
                    : result.status === "verified"
                    ? "Telefon verificat"
                    : "Expirat"
                }`
              : `Existing invitation for ${friendPhone.trim()}. Status: ${
                  result.status === "sent"
                    ? "SMS sent"
                    : result.status === "registered"
                    ? "Friend registered"
                    : result.status === "verified"
                    ? "Phone verified"
                    : "Expired"
                }`
          );
        } else {
          setShowInviteModal(false);
          setFriendPhone("");
          alert(
            currentLang === "ro"
              ? "Invitația a fost trimisă cu succes!"
              : "Invitation sent successfully!"
          );
        }
      } else {
        alert(
          currentLang === "ro"
            ? `Eroare: ${result.error || "Eroare necunoscută"}`
            : `Error: ${result.error || "Unknown error"}`
        );
      }
    } catch (error) {
      console.error("Error sending invite:", error);
      alert(
        currentLang === "ro"
          ? "Eroare la trimiterea invitației"
          : "Error sending invitation"
      );
    } finally {
      setIsSendingInvite(false);
    }
  };

  // Loading
  if (offer === undefined) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-10 h-10 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  // Ofertă negăsită
  if (offer === null) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center px-4">
        <WarningCircle size={64} className="text-error mb-4" />
        <h1 className="text-xl font-bold text-text mb-2">
          {currentLang === "ro" ? "Ofertă negăsită" : "Offer not found"}
        </h1>
        <Link
          to="/app"
          className="mt-4 px-6 py-3 bg-primary text-white rounded-xl font-semibold"
        >
          {currentLang === "ro" ? "Înapoi la oferte" : "Back to offers"}
        </Link>
      </div>
    );
  }

  // Formatare zile valide
  const dayNames = currentLang === "ro"
    ? ["Dum", "Lun", "Mar", "Mie", "Joi", "Vin", "Sâm"]
    : ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  const validDaysText = offer.validDays
    ?.map((day) => dayNames[day])
    .join(", ");


  return (
    <div className="h-screen flex flex-col bg-background overflow-hidden relative">
      {/* Hero Image Background - în spatele header-ului */}
      <div 
        className="fixed inset-0 bg-cover bg-center"
        style={{
          zIndex: 0,
          height: 'calc(35vh + 160px)',
          backgroundImage: (offer.partner as any)?.carouselImageUrl 
            ? `url(${(offer.partner as any).carouselImageUrl})`
            : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b  via-transparent to-black/100" />
      </div>

      <OfferDetailHeader
        isFavorite={isFavorite === true}
        onToggleFavorite={handleToggleFavorite}
      />

      {/* Content Container */}
      <div className="relative flex-1 overflow-hidden pt-[80px]" style={{ zIndex: 1 }}>

        {/* Bottom Sheet - Fixed */}
        <div className="fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl shadow-2xl border-t-2 border-text/20 z-30 flex flex-col" style={{ height: '60vh' }}>
          {/* Title peste bottom sheet - vizibil peste card */}
          {offer.partner?.name && (
            <div className="px-6 -mt-12 mb-4 relative z-20 flex-shrink-0">
              <p className="text-3xl md:text-4xl font-bold text-white leading-tight drop-shadow-xl">
                {offer.partner.name}
              </p>
            </div>
          )}
          
          {/* Header Bottom Sheet - Business Info - Fixed */}
          <div className="px-6 pt-6 pb-6 flex-shrink-0">
            <div className="space-y-3">

            {/* Address + Navigation */}
              {(offer.partner?.address || offer.partner?.city || (offer.partner?.lat && offer.partner?.lng)) && (
                <div className="flex items-center gap-2 text-sm text-text-muted">
                  <MapPin size={16} className="flex-shrink-0 mt-0.5 text-primary" weight="fill" />
                  <span className="text-black">
                    {offer.partner?.address}
                    {(offer.partner?.address && offer.partner?.city) && ', '}
                    {offer.partner?.city}
                  </span>
                  {(offer.partner?.lat && offer.partner?.lng) && (
                    <>
                      <button
                        onClick={() => setShowNavigationOptions(true)}
                        className="flex items-center gap-1 bg-primary/10 p-2 border border-primary/20 rounded-lg text-primary font-medium  hover:text-primary-dark transition-colors"
                        type="button"
                      >
                        <Compass size={16} className="flex-shrink-0" />
                        <span>{currentLang === "ro" ? "Navighează" : "Get Direction"}</span>
                      </button>
                    </>
                  )}
                </div>
                
              )}
                 {/* Phone - Mutat sus */}
                 {offer.partner?.contactPhone && (
                <div className="flex items-center gap-2 text-sm text-text-muted">
                  <Phone size={16} className="flex-shrink-0 text-primary" weight="fill" />
                  <a
                    href={`tel:${offer.partner.contactPhone}`}
                    className="font-medium text-primary hover:text-primary-dark transition-colors"
                  >
                    {offer.partner.contactPhone}
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* Scrollable Content */}
          <div className="overflow-y-auto px-6 pb-4 flex-1 min-h-0">
            <div className="space-y-3">
              {/* Detalii oferta */}
              {(offer.description && offer.description !== offer.shortDescription) && (
                <div>
                  <h6 className="font-semibold text-text mb-2">
                    {currentLang === "ro" ? "Detalii ofertă" : "Offer details"}
                  </h6>
                </div>
              )}

              {/* Detalii tehnice - O singură coloană */}
              {(validDaysText || offer.validHoursStart || offer.usesPerMonth) && (
                <div className="space-y-3">
                  {/* Utilizări */}
                  {offer.usesPerMonth && (
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 bg-accent/20 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Calendar size={18} className="text-accent-dark" />
                      </div>
                      <div className="flex-1">
                        <p className="text-xs text-text-muted mb-0.5">
                          {currentLang === "ro" ? "Utilizări pe lună" : "Uses per month"}
                        </p>
                        <p className="font-medium text-text text-sm">{offer.usesPerMonth}</p>
                      </div>
                    </div>
                  )}

                  {/* Zile valide */}
                  {validDaysText && (
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 bg-accent/20 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Calendar size={18} className="text-accent-dark" />
                      </div>
                      <div className="flex-1">
                        <p className="text-xs text-text-muted mb-0.5">
                          {currentLang === "ro" ? "Zile valide" : "Valid days"}
                        </p>
                        <p className="font-medium text-text text-sm">{validDaysText}</p>
                      </div>
                    </div>
                  )}

                  {/* Ore valide */}
                  {offer.validHoursStart && offer.validHoursEnd && (
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 bg-accent/20 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Clock size={18} className="text-accent-dark" />
                      </div>
                      <div className="flex-1">
                        <p className="text-xs text-text-muted mb-0.5">
                          {currentLang === "ro" ? "Interval orar" : "Valid hours"}
                        </p>
                        <p className="font-medium text-text text-sm">
                          {offer.validHoursStart} - {offer.validHoursEnd}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Exclusiuni */}
              {offer.exclusions && (
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-accent/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <WarningCircle size={18} className="text-accent-dark" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-text-muted mb-0.5">
                      {currentLang === "ro" ? "Exclusiuni" : "Exclusions"}
                    </p>
                    <p className="font-medium text-text text-sm leading-relaxed">
                      {getLocalizedText(offer.exclusions, currentLang)}
                    </p>
                  </div>
                </div>
              )}
                </div>
              )}


              
            </div>
          </div>

          {/* Buton Principal - Sub informații */}
          <div className="px-6 pb-4 pt-4 flex-shrink-0 ">
            {showWarning && canRedeem?.canRedeem === false ? (
              <div className="flex items-center gap-3 p-4 bg-error/10 rounded-xl mb-3">
                <WarningCircle size={20} className="text-error flex-shrink-0" />
                <p className="text-sm text-error">
                  {canRedeem.reason === "trialLocked" &&
                    (currentLang === "ro"
                      ? "Invită un prieten pentru a debloca trial-ul, apasă pe butonul de mai jos pentru a introduce numărul de telefon al prietenului/prietenei."
                      : "Invite a friend to unlock your trial")}
                  {canRedeem.reason === "dailyLimitReached" &&
                    (currentLang === "ro"
                      ? `Ai atins limita de ${canRedeem.details?.limit} reduceri pe zi`
                      : `You've reached the daily limit of ${canRedeem.details?.limit} redemptions`)}
                  {canRedeem.reason === "categoryCooldown" &&
                    (currentLang === "ro"
                      ? `Trebuie să aștepți ${canRedeem.details?.hoursRemaining}h pentru categoria ${canRedeem.details?.category}`
                      : `Wait ${canRedeem.details?.hoursRemaining}h for ${canRedeem.details?.category} category`)}
                  {canRedeem.reason === "monthlyOfferLimitReached" &&
                    (currentLang === "ro"
                      ? "Ai folosit deja această ofertă luna aceasta"
                      : "You've already used this offer this month")}
                  {canRedeem.reason === "partnerMonthlyLimitReached" &&
                    (currentLang === "ro"
                      ? `Ai folosit deja o reducere la ${canRedeem.details?.partnerName} luna aceasta`
                      : `You've already used a discount at ${canRedeem.details?.partnerName} this month`)}
                  {canRedeem.reason === "membershipExpired" &&
                    (currentLang === "ro"
                      ? "Abonamentul tău a expirat"
                      : "Your membership has expired")}
                  {canRedeem.reason === "freeOfferUsed" &&
                    (currentLang === "ro"
                      ? "Ai folosit deja oferta ta gratuită. Invită un prieten pentru 7 zile trial sau plătește pentru a continua."
                      : "You've already used your free offer. Invite a friend for 7 days trial or pay to continue.")}
                </p>
              </div>
            ) : null}

            {canRedeem?.reason === "trialLocked" ? (
              inviteStatus?.status === "verified" ? (
                <button
                  onClick={handleGenerateQR}
                  className="w-full py-4 bg-primary-gradient text-white font-bold rounded-xl flex items-center justify-center gap-2 shadow-lg shadow-primary/30 hover:shadow-xl hover:shadow-primary/40 transition-all"
                >
                  <CheckCircle size={20} weight="fill" />
                  {currentLang === "ro" ? "Deblochează beneficiul" : "Unlock benefit"}
                </button>
              ) : (
                <button
                  onClick={() => setShowInviteModal(true)}
                  className="w-full py-4 bg-primary-gradient text-white font-bold rounded-xl flex items-center justify-center gap-2 shadow-lg shadow-primary/30 hover:shadow-xl hover:shadow-primary/40 transition-all"
                >
                  <Lock size={20} weight="fill" />
                  {currentLang === "ro" ? "Deblochează beneficiul" : "Unlock benefit"}
                </button>
              )
            ) : canRedeem?.reason === "freeOfferUsed" ? (
              <div className="space-y-3">
                <button
                  onClick={() => setShowInviteModal(true)}
                  className="w-full py-4 bg-primary-gradient text-white font-bold rounded-xl flex items-center justify-center gap-2 shadow-lg shadow-primary/30 hover:shadow-xl hover:shadow-primary/40 transition-all"
                >
                  <Users size={20} weight="fill" />
                  {currentLang === "ro" ? "Invită prieten (7 zile trial)" : "Invite friend (7 days trial)"}
                </button>
                <Link
                  to="/app/membership"
                  className="w-full py-4 bg-success-gradient text-white font-bold rounded-xl flex items-center justify-center gap-2 shadow-lg shadow-success/30 hover:shadow-xl hover:shadow-success/40 transition-all"
                >
                  <CreditCard size={20} weight="fill" />
                  {currentLang === "ro" ? "Plătește acum" : "Pay now"}
                </Link>
              </div>
            ) : (
              <button
                onClick={handleGenerateQR}
                disabled={!canRedeem?.canRedeem}
                className="w-full py-4 bg-primary-gradient text-white font-bold rounded-xl flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-primary/30 hover:shadow-xl hover:shadow-primary/40 transition-all"
              >
                <QrCode size={20} weight="fill" />
                {currentLang === "ro" ? "Deblochează beneficiul" : "Unlock benefit"}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Modal QR */}
      {showQR && qrToken && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm overflow-hidden">
            {/* Header modal */}
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h3 className="font-semibold text-text">
                {currentLang === "ro" ? "Cod QR activ" : "Active QR code"}
              </h3>
              <button
                onClick={() => {
                  setShowQR(false);
                  setQrToken(null);
                }}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X size={20} className="text-text-muted" />
              </button>
            </div>

            {/* QR Code */}
            <div className="p-6 flex flex-col items-center">
              <div className="bg-white p-4 rounded-xl border-2 border-primary">
                <QRCodeSVG
                  value={qrToken}
                  size={200}
                  level="H"
                  includeMargin={false}
                />
              </div>

              {/* Token text */}
              <p className="mt-4 font-mono text-lg font-bold text-text tracking-wider">
                {qrToken}
              </p>

              {/* Countdown */}
              <div className="mt-4 flex items-center gap-2">
                <div
                  className={`w-3 h-3 rounded-full ${
                    countdown > 30
                      ? "bg-success"
                      : countdown > 10
                        ? "bg-accent"
                        : "bg-error animate-pulse"
                  }`}
                />
                <span
                  className={`font-medium ${
                    countdown <= 10 ? "text-error" : "text-text"
                  }`}
                >
                  {countdown}s
                </span>
              </div>

              {/* Progress bar */}
              <div className="w-full mt-3 h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all duration-1000 ${
                    countdown > 30
                      ? "bg-success"
                      : countdown > 10
                        ? "bg-accent"
                        : "bg-error"
                  }`}
                  style={{ width: `${(countdown / 60) * 100}%` }}
                />
              </div>

              <p className="mt-4 text-sm text-text-muted text-center">
                {currentLang === "ro"
                  ? "Arată acest cod partenerului pentru a valida reducerea"
                  : "Show this code to the partner to validate your discount"}
              </p>
            </div>

            {/* Footer */}
            <div className="p-4 bg-gray-50 border-t border-border">
              <div className="flex items-center gap-2 text-sm text-text-muted">
                <CheckCircle size={16} className="text-success" />
                <span>{offer.partner?.name}</span>
                <span>•</span>
                <span>
                  {offer.discountType === "percent"
                    ? `${offer.discountValue}%`
                    : getLocalizedText(offer.benefitText, currentLang)}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Navigation Options Modal */}
      {showNavigationOptions && offer && offer.partner?.lat && offer.partner?.lng && (
        <div className="fixed inset-0 bg-black/50 flex items-end justify-center z-50 p-4">
          <div className="bg-white rounded-t-2xl w-full max-w-md overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h3 className="font-semibold text-text">
                {currentLang === "ro" ? "Alege aplicația de navigare" : "Choose navigation app"}
              </h3>
              <button
                onClick={() => setShowNavigationOptions(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X size={20} className="text-text-muted" />
              </button>
            </div>

            {/* Options */}
            <div className="p-4 space-y-2">
              {/* Google Maps */}
              <a
                href={`https://www.google.com/maps/dir/?api=1&destination=${offer.partner.lat},${offer.partner.lng}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setShowNavigationOptions(false)}
                className="flex items-center gap-3 p-4 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
              >
                <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center p-2 shadow-sm">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2C7.582 2 4 5.582 4 10c0 5.25 8 13 8 13s8-7.75 8-13c0-4.418-3.582-8-8-8zm0 11c-1.657 0-3-1.343-3-3s1.343-3 3-3 3 1.343 3 3-1.343 3-3 3z" fill="#4285F4"/>
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-text">Google Maps</p>
                  <p className="text-sm text-text-muted">
                    {currentLang === "ro" ? "Deschide în Google Maps" : "Open in Google Maps"}
                  </p>
                </div>
                <ArrowSquareOut size={20} className="text-text-muted" />
              </a>

              {/* Waze */}
              <a
                href={`https://waze.com/ul?ll=${offer.partner.lat},${offer.partner.lng}&navigate=yes`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setShowNavigationOptions(false)}
                className="flex items-center gap-3 p-4 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
              >
                <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center p-2 shadow-sm">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2L13.41 8.59L20 10L13.41 11.41L12 18L10.59 11.41L4 10L10.59 8.59L12 2Z" fill="#33CCFF"/>
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-text">Waze</p>
                  <p className="text-sm text-text-muted">
                    {currentLang === "ro" ? "Deschide în Waze" : "Open in Waze"}
                  </p>
                </div>
                <ArrowSquareOut size={20} className="text-text-muted" />
              </a>

              {/* Apple Maps (doar pe iOS/Mac) */}
              {typeof window !== "undefined" && /iPad|iPhone|iPod|Mac/.test(navigator.userAgent) && (
                <a
                  href={`maps://maps.apple.com/?daddr=${offer.partner.lat},${offer.partner.lng}`}
                  onClick={() => setShowNavigationOptions(false)}
                  className="flex items-center gap-3 p-4 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
                >
                  <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center p-2 shadow-sm">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 2C7.582 2 4 5.582 4 10c0 5.25 8 13 8 13s8-7.75 8-13c0-4.418-3.582-8-8-8zm0 11c-1.657 0-3-1.343-3-3s1.343-3 3-3 3 1.343 3 3-1.343 3-3 3z" fill="#000000"/>
                    </svg>
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-text">Apple Maps</p>
                    <p className="text-sm text-text-muted">
                      {currentLang === "ro" ? "Deschide în Apple Maps" : "Open in Apple Maps"}
                    </p>
                  </div>
                  <ArrowSquareOut size={20} className="text-text-muted" />
                </a>
              )}

              {/* Fallback: coordonate pentru copiere */}
              {offer.partner?.lat && offer.partner?.lng && (
                <button
                  onClick={() => {
                    if (offer.partner?.lat && offer.partner?.lng) {
                      navigator.clipboard.writeText(`${offer.partner.lat}, ${offer.partner.lng}`);
                      alert(
                        currentLang === "ro"
                          ? "Coordonatele au fost copiate!"
                          : "Coordinates copied!"
                      );
                      setShowNavigationOptions(false);
                    }
                  }}
                  className="w-full flex items-center gap-3 p-4 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors text-left"
                >
                  <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center">
                    <MapPin size={20} className="text-text-muted" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-text">
                      {currentLang === "ro" ? "Copiază coordonatele" : "Copy coordinates"}
                    </p>
                    {offer.partner?.lat && offer.partner?.lng && (
                      <p className="text-sm text-text-muted">
                        {offer.partner.lat.toFixed(6)}, {offer.partner.lng.toFixed(6)}
                      </p>
                    )}
                  </div>
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal Invitare Prieten */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm overflow-hidden">
            {/* Header modal */}
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h3 className="font-semibold text-text">
                {currentLang === "ro" ? "Deblochează oferta" : "Unlock offer"}
              </h3>
              <button
                onClick={() => {
                  setShowInviteModal(false);
                  setFriendPhone("");
                }}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X size={20} className="text-text-muted" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              {/* Status invitație existentă */}
              {inviteStatus && inviteStatus.status !== "expired" && (
                <div className={`p-4 rounded-xl mb-4 ${
                  inviteStatus.status === "verified"
                    ? "bg-success/10"
                    : inviteStatus.status === "registered"
                    ? "bg-accent/10"
                    : "bg-primary/10"
                }`}>
                  <div className="flex items-start gap-3">
                    {inviteStatus.status === "verified" ? (
                      <CheckCircle size={20} className="text-success flex-shrink-0 mt-0.5" />
                    ) : (
                      <Clock size={20} className="text-primary flex-shrink-0 mt-0.5" />
                    )}
                    <div className="flex-1">
                      <p className={`text-sm font-medium ${
                        inviteStatus.status === "verified"
                          ? "text-success"
                          : "text-text"
                      }`}>
                        {inviteStatus.status === "sent" &&
                          (currentLang === "ro"
                            ? `Invitație trimisă la ${inviteStatus.friendPhone}`
                            : `Invitation sent to ${inviteStatus.friendPhone}`)}
                        {inviteStatus.status === "registered" &&
                          (currentLang === "ro"
                            ? `Prietenul s-a înregistrat cu ${inviteStatus.friendPhone}`
                            : `Friend registered with ${inviteStatus.friendPhone}`)}
                        {inviteStatus.status === "verified" &&
                          (currentLang === "ro"
                            ? `Telefon verificat - trial deblocat!`
                            : `Phone verified - trial unlocked!`)}
                      </p>
                      {inviteStatus.status === "registered" && (
                        <p className="text-xs text-text-muted mt-1">
                          {currentLang === "ro"
                            ? "Așteaptă verificarea telefonului"
                            : "Waiting for phone verification"}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <p className="text-sm text-text-light mb-4">
                {inviteStatus?.status === "verified"
                  ? (currentLang === "ro"
                      ? "Trial-ul tău este deblocat! Poți folosi ofertele."
                      : "Your trial is unlocked! You can use offers.")
                  : currentLang === "ro"
                  ? "Introdu numărul de telefon al prietenului/prietenei. Vom trimite un SMS cu codul de invitație. După ce prietenul se înregistrează și iși face cont cu acelasi număr de telefon, trial-ul tău se va debloca automat."
                  : "Enter your friend's phone number. We'll send an SMS with the invite code. After your friend registers and verifies their phone, your trial will unlock automatically."}
              </p>

              <div className="space-y-4">
                <div>
                  <label
                    htmlFor="friendPhone"
                    className="block text-sm font-medium text-text mb-2"
                  >
                    {currentLang === "ro" ? "Număr telefon" : "Phone number"}
                  </label>
                  <div className="relative">
                    <Phone size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted" />
                    <input
                      id="friendPhone"
                      type="tel"
                      value={friendPhone}
                      onChange={(e) => setFriendPhone(e.target.value)}
                      placeholder={currentLang === "ro" ? "0712345678" : "0712345678"}
                      className="w-full pl-12 pr-4 py-3 bg-white border border-border rounded-xl text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                    />
                  </div>
                </div>

                {inviteStatus?.status !== "verified" && (
                  <button
                    onClick={handleSendInvite}
                    disabled={!friendPhone.trim() || isSendingInvite}
                    className="w-full py-3 bg-primary text-white font-semibold rounded-xl hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSendingInvite
                      ? currentLang === "ro"
                        ? "Se trimite..."
                        : "Sending..."
                      : inviteStatus && inviteStatus.status !== "expired"
                      ? currentLang === "ro"
                        ? "Trimite la alt număr"
                        : "Send to another number"
                      : currentLang === "ro"
                      ? "Trimite invitația"
                      : "Send invitation"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
